import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The value, but only after it has stopped changing for `delayMs`.
 *
 * For inputs that drive a server round trip — a search box or a filter bar
 * whose every interaction would otherwise be a query. Local filtering does not
 * need this and should not use it: the delay is only worth paying for a
 * request.
 *
 * `keyOf` exists for object values. A params object is rebuilt on every render,
 * so its identity always differs and the effect below would restart its timer
 * forever, never firing. Pass `JSON.stringify` (or any stable projection) to
 * compare by value instead.
 *
 * Returns the settled value plus `reset`, which adopts a value NOW and cancels
 * any pending timer. A "clear" button needs that: without it the in-flight
 * timer lands a moment later and puts the old value straight back, so the
 * control appears to clear and then undo itself.
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs: number,
  keyOf: (value: T) => string | number = (v) => String(v),
): [T, (next: T) => void] {
  const [debounced, setDebounced] = useState(value);

  // The effect below depends on the value's KEY, not the value, so it would
  // otherwise close over a stale `value` whenever two different values share a
  // key. Reading through a ref keeps the timer honest either way.
  const latest = useRef(value);
  latest.current = value;

  const timer = useRef<ReturnType<typeof setTimeout>>();
  const key = keyOf(value);

  useEffect(() => {
    timer.current = setTimeout(() => setDebounced(latest.current), delayMs);
    return () => clearTimeout(timer.current);
  }, [key, delayMs]);

  const reset = useCallback((next: T) => {
    clearTimeout(timer.current);
    latest.current = next;
    setDebounced(next);
  }, []);

  return [debounced, reset];
}
